// PROTOTYPE (as mobile's prototypeAgents.tsx, th:4cad1dd4/60): static
// stand-ins for the "Agents for managers" spec's default agents, which the
// backend doesn't serve yet. They run on the Primary Agent: each is one
// Primary Agent chat per manager, started with the agent's canned prompt.
// Every canned name, prompt and description lives here so the prototype
// comes out by deleting this file and its imports.
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { OpusAgentAvatarKind } from "@src/types.generated";
import type { AgentAvatarFields } from "@src/features/agents/AgentAvatar";
import { noopStorage, safeLocalStorage } from "./useOpusAgentDrawerStore";

export type ManagerAgentKey =
  | "location-health"
  | "daily-summary"
  | "new-hire-coach";

export type ManagerAgent = {
  key: ManagerAgentKey;
  name: string;
  prompt: string;
  // The details popover's line under the name.
  summary: string;
  // The opening message's and details popover's "What it does" list.
  capabilities: readonly string[];
  // As mobile's SPEC_AGENT_COLORS: three distinct brand colours.
  color: "teal" | "amber" | "purple";
};

export const MANAGER_AGENTS: readonly ManagerAgent[] = [
  {
    key: "location-health",
    name: "Location health",
    prompt:
      "How is my location doing this week on training, checklists and tasks compared with the others?",
    summary: "How your location is doing on what Opus measures.",
    capabilities: [
      "Tracks training completion, overdue assignments and onboarding progress",
      "Checks checklists done on time, audit scores and open follow-ups",
      "Watches check-ins and team engagement",
      "Compares you with the business average and your rank, without naming other locations",
      "Shows your locations side by side if you oversee several",
    ],
    color: "teal",
  },
  {
    key: "daily-summary",
    name: "Daily summary",
    prompt:
      "Give me my daily summary: what's due today, who needs a nudge, what went wrong yesterday, and who to recognize.",
    summary: "What needs your attention today.",
    capabilities: [
      "What's due today",
      "Who needs a nudge",
      "What went wrong yesterday",
      "Who to recognize",
    ],
    color: "amber",
  },
  {
    key: "new-hire-coach",
    name: "New hire coach",
    prompt:
      "Which new hires need attention this week, and what should I cover in their upcoming check-ins?",
    summary: "Follows each new hire through their first 90 days.",
    capabilities: [
      "Welcomes them on day one with a message from you",
      "Flags anyone falling behind on onboarding training, and nudges them",
      "Reminds you when a check-in is due and helps you prepare it",
      "Calls out milestones worth recognizing",
    ],
    color: "purple",
  },
];

// The agent panel's id for a manager agent; never a backend agent's UUID.
const PANEL_ID_PREFIX = "manager-agent:";

export const managerAgentPanelId = (key: ManagerAgentKey): string =>
  `${PANEL_ID_PREFIX}${key}`;

export const findManagerAgent = (
  panelId: string | null | undefined,
): ManagerAgent | undefined =>
  panelId?.startsWith(PANEL_ID_PREFIX)
    ? MANAGER_AGENTS.find((agent) => managerAgentPanelId(agent.key) === panelId)
    : undefined;

// Drawn as a bot in its colour by AgentAvatar / AgentBot; the panel id keys
// its live orb state.
export const managerAgentAvatar = (agent: ManagerAgent): AgentAvatarFields => ({
  id: managerAgentPanelId(agent.key),
  avatarKind: OpusAgentAvatarKind.Bot,
  avatarBotColor: agent.color,
});

type ManagerAgentsState = {
  // Whose entries these are: another user's write replaces them.
  userId: string | null;
  // Each agent's one chat with this user.
  chatIds: Partial<Record<ManagerAgentKey, string>>;
  // The Home card's X.
  introDismissed: boolean;
  setChatId: (userId: string, key: ManagerAgentKey, chatId: string) => void;
  dismissIntro: (userId: string) => void;
  clear: () => void;
};

const isKey = (value: string): value is ManagerAgentKey =>
  MANAGER_AGENTS.some((agent) => agent.key === value);

// Signed-in-user data: cleared in logout.tsx.
export const useManagerAgentsStore = create<ManagerAgentsState>()(
  persist(
    (set) => ({
      userId: null,
      chatIds: {},
      introDismissed: false,
      setChatId: (userId, key, chatId) =>
        set((state) => ({
          userId,
          chatIds: {
            ...(state.userId === userId ? state.chatIds : {}),
            [key]: chatId,
          },
          introDismissed: state.userId === userId && state.introDismissed,
        })),
      dismissIntro: (userId) =>
        set((state) => ({
          userId,
          chatIds: state.userId === userId ? state.chatIds : {},
          introDismissed: true,
        })),
      clear: () => set({ userId: null, chatIds: {}, introDismissed: false }),
    }),
    {
      name: "opus-manager-agents",
      version: 1,
      storage: createJSONStorage(() =>
        typeof window === "undefined" ? noopStorage : safeLocalStorage,
      ),
      partialize: ({ userId, chatIds, introDismissed }) => ({
        userId,
        chatIds,
        introDismissed,
      }),
      merge: (persisted, current) => {
        const stored = persisted as {
          userId?: unknown;
          chatIds?: unknown;
          introDismissed?: unknown;
        } | null;
        const userId =
          typeof stored?.userId === "string" && stored.userId
            ? stored.userId
            : null;
        const chatIds: Partial<Record<ManagerAgentKey, string>> = {};
        if (userId && stored?.chatIds && typeof stored.chatIds === "object")
          for (const [key, chatId] of Object.entries(stored.chatIds))
            if (isKey(key) && typeof chatId === "string" && chatId)
              chatIds[key] = chatId;
        return {
          ...current,
          userId,
          chatIds,
          introDismissed: !!userId && stored?.introDismissed === true,
        };
      },
    },
  ),
);

// This user's chat with the agent: undefined = none started yet.
export const useManagerAgentChatId = (
  userId: string | null,
  key: ManagerAgentKey,
): string | undefined =>
  useManagerAgentsStore((state) =>
    userId && state.userId === userId ? state.chatIds[key] : undefined,
  );

// Every manager agent's chat for this user, which Ask Opus's own
// most-recent lookup skips.
export const useManagerAgentChatIds = (userId: string | null): string[] => {
  const chatIds = useManagerAgentsStore((state) =>
    userId && state.userId === userId ? state.chatIds : null,
  );
  return chatIds ? Object.values(chatIds) : [];
};

export const clearManagerAgentsStore = (): void =>
  useManagerAgentsStore.getState().clear();
